import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AiCheckSummary, AiInsight, LateRisk } from '@/types'

// MPG AI on the console's own screens (Duncan 2026-10-26). A check or a brief
// is written on the server's queue, so while it is queued or running these
// poll; once it is ready or failed they stop.

const POLL_MS = 4000

export const aiKeys = {
  brief: ['ai', 'brief'] as const,
  check: (orderId: number) => ['ai', 'check', orderId] as const,
  checks: (orderIds: number[]) => ['ai', 'checks', orderIds] as const,
  risks: ['ai', 'risks'] as const,
  orderRisk: (orderId: number) => ['ai', 'risk', orderId] as const,
}

const busy = (insight?: AiInsight | null) => insight?.status === 'queued' || insight?.status === 'running'

/**
 * Keep polling while the tab is hidden, and look again when it is shown. The
 * app turns window-focus refetching off everywhere (lib/queryClient), and
 * React Query pauses intervals in a hidden tab, so a brief that finished while
 * the admin was in another tab never appeared (2026-10-26).
 */
const watchWhileWriting = { refetchIntervalInBackground: true, refetchOnWindowFocus: true } as const

export function useBrief() {
  return useQuery({
    queryKey: aiKeys.brief,
    queryFn: () => api.get<{ data: AiInsight | null }>('/admin/ai/brief').then((r) => r.data.data),
    refetchInterval: (query) => (busy(query.state.data) ? POLL_MS : false),
    ...watchWhileWriting,
  })
}

export function useOrderCheck(orderId: number, enabled = true) {
  return useQuery({
    queryKey: aiKeys.check(orderId),
    queryFn: () => api.get<{ data: AiInsight | null }>(`/admin/ai/orders/${orderId}/check`).then((r) => r.data.data),
    enabled,
    refetchInterval: (query) => (busy(query.state.data) ? POLL_MS : false),
    ...watchWhileWriting,
  })
}

/** The newest check per order, for a list; polls while any of them is still being written. */
export function useOrderChecks(orderIds: number[], enabled = true) {
  return useQuery({
    queryKey: aiKeys.checks(orderIds),
    queryFn: () =>
      api
        .get<{ data: Record<number, AiCheckSummary> }>('/admin/ai/checks', { params: { order_ids: orderIds } })
        .then((r) => r.data.data),
    enabled: enabled && orderIds.length > 0,
    refetchInterval: (query) =>
      Object.values(query.state.data ?? {}).some((c) => c.status === 'queued' || c.status === 'running') ? POLL_MS : false,
    ...watchWhileWriting,
  })
}

/** Every open job at risk, keyed by job id. Arithmetic on the server, so it answers at once. */
export function useLateRisks(enabled = true) {
  return useQuery({
    queryKey: aiKeys.risks,
    queryFn: () => api.get<{ data: LateRisk[] }>('/admin/ai/risks').then((r) => r.data.data),
    enabled,
    staleTime: 60_000,
    select: (rows) => new Map(rows.map((row) => [row.job_id, row])),
  })
}

export function useOrderRisk(orderId: number, enabled = true) {
  return useQuery({
    queryKey: aiKeys.orderRisk(orderId),
    queryFn: () => api.get<{ data: LateRisk | null }>(`/admin/ai/orders/${orderId}/risk`).then((r) => r.data.data),
    enabled,
    staleTime: 60_000,
  })
}
